/**
 * PWAInstallPrompt — non-intrusive bottom banner that suggests installing the
 * PWA to the home screen.
 *  - On Chromium/Android: listens for `beforeinstallprompt`, defers it, then
 *    triggers the native install UI when the user taps "Install".
 *  - On iOS Safari (where `beforeinstallprompt` is not supported): shows a
 *    short "Tap Share → Add to Home Screen" tooltip.
 *  - Dismissal is sticky for 7 days via localStorage so it doesn't nag users.
 *  - Auto-hides if already running as a standalone PWA.
 */
import React, { useEffect, useState } from 'react';
import { X, Download, Share } from 'lucide-react';
import { Button } from './ui/button';

const DISMISS_KEY = 'loveall:pwa_install_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isiOS() {
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function dismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return ts && Date.now() - ts < DISMISS_TTL_MS;
  } catch { return false; }
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS fallback — show the share-sheet hint after a short delay so it doesn't
    // pop immediately on first paint.
    let iosTimer;
    if (isiOS()) {
      iosTimer = setTimeout(() => {
        if (!isStandalone() && !dismissedRecently()) {
          setIosHint(true);
          setVisible(true);
        }
      }, 4000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setVisible(false);
      } else {
        dismiss(); // user said no — respect 7-day cooldown
      }
    } catch { /* ignore */ }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-20 md:bottom-6 z-[60] w-[calc(100%-1.5rem)] max-w-md rounded-xl border border-primary/40 bg-background/95 backdrop-blur-md shadow-2xl p-3 flex items-center gap-3 safe-bottom"
      data-testid="pwa-install-prompt"
    >
      <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center text-primary">
        {iosHint ? <Share className="h-5 w-5" /> : <Download className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold leading-tight">Install LoveAll</p>
        <p className="text-xs text-muted-foreground leading-snug mt-0.5">
          {iosHint
            ? 'Tap Share → "Add to Home Screen" for full-screen play.'
            : 'Add to home screen for an app-like experience.'}
        </p>
      </div>
      {!iosHint && (
        <Button
          size="sm"
          onClick={install}
          className="h-9 px-3 font-semibold"
          data-testid="pwa-install-accept-btn"
        >
          Install
        </Button>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="flex-shrink-0 h-8 w-8 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground"
        data-testid="pwa-install-dismiss-btn"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
